// src/ui/screens/chats/widgets/MessageInput.jsx

import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker from 'emoji-picker-react';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const MAX_RECORDING_DURATION = 300; // 5 minutes — matches Flutter's cap

const getSupportedAudioMimeType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder) return '';
  const candidates = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
};

const extensionForMimeType = (mimeType) => {
  if (!mimeType) return 'webm';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
};

// ── Voice Recorder ──────────────────────────────────────────────────────────

const VoiceRecorder = ({ onRecordingComplete, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [recordedFile, setRecordedFile] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const mimeTypeRef = useRef('');
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);

  // ── Format duration ──────────────────────────────────────────────────────

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // ── Start recording ─────────────────────────────────────────────────────

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      // mediaRecorder.mimeType reflects what the browser actually chose,
      // which may differ slightly from the requested candidate (e.g. added codecs param).
      mimeTypeRef.current = mediaRecorder.mimeType || mimeType || 'audio/webm';
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const actualType = mediaRecorderRef.current?.mimeType || mimeTypeRef.current || 'audio/webm';
        const ext = extensionForMimeType(actualType);
        const blob = new Blob(chunksRef.current, { type: actualType });
        const url = URL.createObjectURL(blob);
        const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: actualType });
        setRecordedFile(file);
        setRecordedUrl(url);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev >= MAX_RECORDING_DURATION) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Recording error:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  // ── Stop recording ─────────────────────────────────────────────────────

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // ── Play recording ─────────────────────────────────────────────────────

  const playRecording = () => {
    if (!recordedUrl) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      const audio = new Audio(recordedUrl);
      audioRef.current = audio;

      audio.addEventListener('timeupdate', () => {
        setPlaybackPosition(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlaybackPosition(0);
      });

      audio.play();
      setIsPlaying(true);
    }
  };

  // ── Delete recording ──────────────────────────────────────────────────

  const deleteRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedFile(null);
    setRecordedUrl(null);
    setPlaybackPosition(0);
    setIsPlaying(false);
  };

  // ── Send recording ────────────────────────────────────────────────────

  const sendRecording = () => {
    if (recordedFile && recordingDuration > 0) {
      onRecordingComplete(recordedFile, recordingDuration);
      onClose();
    }
  };

  // ── Cleanup ─────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [recordedUrl]);

  // ── Render ──────────────────────────────────────────────────────────────

  const progress = Math.min(recordingDuration / MAX_RECORDING_DURATION, 1);

  return (
    <div
      className="p-5 rounded-3xl w-full max-w-sm"
      style={{ backgroundColor: COLORS.card }}
    >
      {!isRecording && !recordedFile && (
        <div className="flex flex-col items-center">
          <button
            onClick={startRecording}
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${COLORS.orange}33` }}
          >
            <svg className="w-10 h-10 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
          <p className="text-white text-sm mt-4">Tap to record</p>
          <p className="text-white/50 text-xs mt-2">Maximum recording time: 5 minutes</p>
        </div>
      )}

      {isRecording && (
        <div className="flex flex-col items-center">
          <button
            onClick={stopRecording}
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,0,0,0.2)' }}
          >
            <svg className="w-10 h-10 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
          <p className="text-white text-sm mt-4">Recording {formatDuration(recordingDuration)}</p>
          <div className="w-48 h-1 rounded-full mt-2 bg-white/20">
            <div
              className="h-full rounded-full bg-red-500 transition-all"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      )}

      {recordedFile && (
        <div>
          <div className="flex items-center gap-2">
            <button
              onClick={playRecording}
              className="text-white p-2"
            >
              <svg className="w-9 h-9" fill="currentColor" viewBox="0 0 24 24">
                {isPlaying ? (
                  <>
                    <path d="M6 4h4v16H6z" />
                    <path d="M14 4h4v16h-4z" />
                  </>
                ) : (
                  <path d="M8 5v14l11-7z" />
                )}
              </svg>
            </button>
            <div className="flex-1">
              <input
                type="range"
                min="0"
                max={recordingDuration}
                value={playbackPosition}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setPlaybackPosition(val);
                  if (audioRef.current) {
                    audioRef.current.currentTime = val;
                  }
                }}
                className="w-full accent-filatom-primary"
                style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
              />
              <div className="flex justify-between text-white/50 text-xs">
                <span>{formatDuration(playbackPosition)}</span>
                <span>{formatDuration(recordingDuration)}</span>
              </div>
            </div>
            <button
              onClick={deleteRecording}
              className="text-red-500 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={onClose}
              className="text-white/50 px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={sendRecording}
              className="px-6 py-2 rounded-xl text-white font-medium"
              style={{ backgroundColor: COLORS.orange }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Message Input ────────────────────────────────────────────────────────────

export const MessageInput = ({
  controller,
  focusNode,
  isSending,
  onSend,
  onImageSelected,
  onVoiceRecorded,
  onEmojiToggle,
  showEmojiPicker,
  pendingImages = [],
  onRemovePendingMedia,
  replyingTo = null,
  onCancelReply = null,
  currentUserId = null,
}) => {
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [text, setText] = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);

  const hasText = text.trim().length > 0;
  const hasImages = pendingImages && pendingImages.length > 0;
  const showSendButton = hasText || hasImages;

  // ── Update text from controller ─────────────────────────────────────────

  useEffect(() => {
    // Initial sync
    if (controller && controller.text !== undefined) {
      setText(controller.text || '');
    }

    // Listen to controller changes
    const handleChange = () => {
      if (controller && controller.text !== undefined) {
        setText(controller.text || '');
      }
    };

    // Try to add event listener if controller supports it
    if (controller && typeof controller.addEventListener === 'function') {
      controller.addEventListener('input', handleChange);
      return () => {
        controller.removeEventListener('input', handleChange);
      };
    }
  }, [controller]);

  // ── Generate image previews ──────────────────────────────────────────────

  useEffect(() => {
    const generatePreviews = async () => {
      const previews = [];
      for (const image of pendingImages) {
        if (image instanceof File || image instanceof Blob) {
          const url = URL.createObjectURL(image);
          previews.push(url);
        } else if (image && typeof image === 'object' && image.path) {
          // If it's an object with a path, try to create a URL
          try {
            const file = new File([image], image.name || 'image.jpg');
            const url = URL.createObjectURL(file);
            previews.push(url);
          } catch (e) {
            console.error('Error creating preview for image:', e);
          }
        }
      }
      setImagePreviews(previews);

      // Cleanup old previews when component unmounts or pendingImages changes
      return () => {
        previews.forEach((url) => URL.revokeObjectURL(url));
      };
    };

    if (pendingImages && pendingImages.length > 0) {
      generatePreviews();
    } else {
      // Cleanup old previews when no images
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      setImagePreviews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingImages]);

  // ── Handle emoji select ─────────────────────────────────────────────────

  const handleEmojiSelect = (emojiData) => {
    const emoji = emojiData.emoji;
    const currentText = controller?.text || text;
    const newText = currentText + emoji;

    if (controller && typeof controller.setValue === 'function') {
      controller.setValue(newText);
    } else if (controller && typeof controller.text !== 'undefined') {
      controller.text = newText;
    }

    setText(newText);
  };

  // ── Handle text change ──────────────────────────────────────────────────

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);

    // Update controller
    if (controller && typeof controller.setValue === 'function') {
      controller.setValue(newText);
    } else if (controller && typeof controller.text !== 'undefined') {
      controller.text = newText;
    }
  };

  // ── Render reply preview ────────────────────────────────────────────────

  const renderReplyPreview = () => {
    if (!replyingTo) return null;

    const isOwn = replyingTo.senderId === currentUserId;
    const senderLabel = isOwn ? 'You' : replyingTo.senderId || 'User';

    let previewText = replyingTo.content || '';
    if (replyingTo.isDeleted) {
      previewText = 'This message was deleted';
    } else if (replyingTo.messageType === 'image') {
      previewText = '📷 Photo';
    } else if (replyingTo.messageType === 'audio') {
      previewText = '🎤 Voice message';
    } else if (previewText.length > 80) {
      previewText = previewText.substring(0, 80) + '…';
    }

    return (
      <div
        className="mx-3 mb-1 px-3 py-2 rounded-xl flex items-center gap-2 border-l-3"
        style={{
          backgroundColor: 'rgba(35,38,44,0.6)',
          borderLeftColor: COLORS.orange,
        }}
      >
        <div className="flex-1 min-w-0">
          <p className="text-filatom-primary text-xs font-semibold">{senderLabel}</p>
          <p className="text-white/65 text-xs truncate">{previewText}</p>
        </div>
        <button onClick={onCancelReply} className="text-white/50 p-1">
          <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  };

  // ── Render media preview ────────────────────────────────────────────────

  const renderMediaPreview = () => {
    if (!hasImages || imagePreviews.length === 0) return null;

    return (
      <div className="mx-3 mb-1 p-2 rounded-xl flex items-center" style={{ backgroundColor: 'rgba(35,38,44,0.5)' }}>
        <div className="flex gap-2 overflow-x-auto">
          {imagePreviews.map((url, index) => (
            <div key={index} className="relative flex-shrink-0">
              <img
                src={url}
                alt={`Pending ${index + 1}`}
                className="w-[60px] h-[60px] rounded-lg object-cover"
              />
              <button
                onClick={onRemovePendingMedia}
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center"
              >
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Handle image selection ─────────────────────────────────────────────
  // Mirrors Flutter's ImagePicker.pickImage(source: gallery) — one image per pick.

  const handleImageSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0 && onImageSelected) {
        onImageSelected(files[0]);
      }
    };
    input.click();
  };

  // ── Handle send ─────────────────────────────────────────────────────────

  const handleSend = () => {
    if (onSend) {
      onSend();
    }
  };

  // ── Handle key down ─────────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="w-full">
      {renderReplyPreview()}
      {renderMediaPreview()}

      <div
        className="w-full"
        style={{
          background: `linear-gradient(to bottom, rgba(8,9,11,0) 0%, rgba(8,9,11,0.8) 70%, rgba(8,9,11,0.98) 100%)`,
        }}
      >
        <div className="px-3 py-2.5">
          <div className="flex items-end gap-2">
            {/* Attachment Button */}
            <button
              onClick={handleImageSelect}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Text Input */}
            <div
              className="flex-1 rounded-full overflow-hidden"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              <div className="flex items-end px-3.5 py-1.5">
                <textarea
                  ref={(el) => {
                    // If controller has a setElement method, use it
                    if (controller && typeof controller.setElement === 'function') {
                      controller.setElement(el);
                    }
                  }}
                  value={text}
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                  placeholder={hasImages ? 'Add a caption...' : 'Message'}
                  className="flex-1 bg-transparent text-white text-sm outline-none resize-none max-h-30 min-h-[40px] py-2 placeholder:text-white/30"
                  rows={1}
                  style={{ height: 'auto' }}
                />

                {/* Emoji Button */}
                <button
                  onClick={() => {
                    if (showEmojiPicker) {
                      onEmojiToggle?.();
                      focusNode?.current?.focus?.();
                    } else {
                      focusNode?.current?.blur?.();
                      onEmojiToggle?.();
                    }
                  }}
                  className="p-1 flex-shrink-0"
                >
                  <svg className="w-5.5 h-5.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Send / Mic Button */}
            {showSendButton ? (
              <button
                onClick={handleSend}
                disabled={isSending}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                style={{ backgroundColor: COLORS.orange }}
              >
                {isSending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            ) : (
              <button
                onClick={() => setShowVoiceRecorder(true)}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'rgba(35,38,44,0.3)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid #23262C',
                }}
              >
                <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
            )}
          </div>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="mt-2" style={{ backgroundColor: COLORS.card }}>
              <EmojiPicker
                onEmojiClick={handleEmojiSelect}
                width="100%"
                height={250}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled
                searchDisabled
              />
            </div>
          )}
        </div>
      </div>

      {/* Voice Recorder Modal */}
      {showVoiceRecorder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setShowVoiceRecorder(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <VoiceRecorder
              onRecordingComplete={onVoiceRecorded}
              onClose={() => setShowVoiceRecorder(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageInput;